<template>
  <div class="user-settings-container">
    <!-- 第一部分：标题栏 -->
    <div class="settings-header">
      <h1 class="title">用户信息</h1>
      
      <el-button
        type="text"
        icon="el-icon-arrow-left"
        @click="goBack"
        class="back-button"
        title="返回上一页"
        :style="{ color: 'rgba(37, 100, 207, 0.8)' }"
      >
        返回
      </el-button>
    </div>
    
    <!-- 第二部分：个人信息卡片 -->
    <user-info-card 
      :user-info="userInfo"
    />

    <!-- 第三部分：管理员表格 -->
    <card-m
      title="管理员信息"
      cardWidth="100%"
      cardHeight="calc(100vh - 110px)"
      maxHeight="unset"
      :show-filter="true" 
      style="height: 100%;"
    >
      <template slot="card-box">
        <div style="height: 100%;"> 
          <card-table
            :total="managementTotal"
            @sizeChange="handleManagementSizeChanges"
            @currentChange="handleManagementCurrentChanges"
            :tableColumn="managementListColumn"
            :tableData="managementData"
            :pageSizes="[15, 30, 50, 100]"
            :showIndex="true"
            :mobileColumns="managementMobileColumns"
            @switchChange="handleSwitchChange" 
            @resetPasswordClick="handleResetPasswordClick" 
          ></card-table>
        </div>
      </template>

      <!-- 筛选框插槽内容 - 左侧为设为管理员，右侧为管理用户信息 -->
      <template slot="card-filter">
        <div class="set-admin-filter-content">
          <div class="set-admin-form">
            <span class="set-admin-label">添加管理员：</span>
            <el-input
              size="mini"
              v-model="setAdminUserCode"
              placeholder="请输入工号"
              clearable
              style="width: 150px; margin-right: 10px;"
              @keyup.enter.native="handleSetAdmin"
            ></el-input>
            <el-button
              type="primary"
              size="mini"
              @click="handleSetAdmin"
              :loading="setAdminLoading"
              :disabled="!setAdminUserCode"
            >
              确定
            </el-button>
          </div>
          <el-button
            type="primary"
            size="mini"
            icon="el-icon-user"
            class="manage-user-button"
            @click="openManageUserDialog"
          >
            管理用户信息
          </el-button>
        </div>
      </template>
    </card-m>



    <!-- 管理用户信息对话框 -->
    <el-dialog
      title="管理用户信息"
      :visible.sync="manageUserDialog.visible"
      width="420px"
      :close-on-click-modal="false"
      @closed="handleManageUserDialogClosed"
    >
      <div>
        <p style="margin-bottom: 12px; color: #606266;">
          请输入要操作的用户工号：
        </p>
        <el-input
          v-model="manageUserDialog.userCode"
          placeholder="请输入用户工号"
          clearable
          style="margin-bottom: 8px;"
          @keyup.enter.native="openConfirmDialog('clearPassword')"
        ></el-input>
        <p style="color: #909399; font-size: 12px; margin: 0;">
          请选择对已注册用户执行「修改用户权限」、「清空用户密码」或「注销用户信息」操作。
        </p>
      </div>
      <div slot="footer" class="dialog-footer">
        <el-button @click="manageUserDialog.visible = false" size="small">取消</el-button>

        <!-- ✅ 修改用户权限：勾选模块后提交（清空勾选 = 传空权限） -->
        <el-button
          type="primary"
          size="small"
          :disabled="!manageUserDialog.userCode.trim()"
          @click="openPermissionDialog"
        >
          修改用户权限
        </el-button>

        <el-button
          type="danger"
          size="small"
          :disabled="!manageUserDialog.userCode.trim()"
          @click="openConfirmDialog('clearPassword')"
        >
          清空用户密码
        </el-button>
        <el-button
          type="danger"
          size="small"
          :disabled="!manageUserDialog.userCode.trim()"
          @click="openConfirmDialog('cancelAccount')"
        >
          注销用户信息
        </el-button>

      </div>
    </el-dialog>

    <!-- ✅ 修改用户权限对话框（多选：项目 / 合同 / 生产 / 产品 / 部件 / 交付 → 1 / 2 / 3 / 4 / 5 / 6） -->
    <el-dialog
      title="修改用户权限"
      :visible.sync="permissionDialog.visible"
      width="420px"
      :close-on-click-modal="false"
      append-to-body
      @closed="handlePermissionDialogClosed"
    >
      <div>
        <p style="margin-bottom: 12px; color: #606266;">
          目标用户工号：
          <strong style="color: #2564CF;">{{ manageUserDialog.userCode }}</strong>
        </p>

        <el-checkbox-group v-model="permissionDialog.checked">
          <el-checkbox
            v-for="opt in permissionOptions"
            :key="opt.value"
            :label="opt.value"
            style="display: block; margin-left: 0; line-height: 28px;"
          >
            {{ opt.label }}
          </el-checkbox>
        </el-checkbox-group>

        <p style="color: #909399; font-size: 12px; margin: 12px 0 0;">
          将提交 urlPermission =
          <strong style="color: #606266;">{{ permissionString || '（空，无模块权限）' }}</strong>
        </p>
      </div>
      <div slot="footer" class="dialog-footer">
        <el-button size="small" @click="permissionDialog.visible = false">取消</el-button>
        <el-button
          type="primary"
          size="small"
          :loading="permissionLoading"
          @click="submitUserPermission"
        >
          确定
        </el-button>
      </div>
    </el-dialog>

    <!-- 操作确认对话框（输入工号两次确认） -->
    <el-dialog
      :title="confirmDialog.action === 'clearPassword' ? '确认清空用户密码' : '确认注销用户信息'"
      :visible.sync="confirmDialog.visible"
      width="420px"
      :close-on-click-modal="false"
      @closed="handleConfirmDialogClosed"
    >
      <div>
        <el-alert
          :title="confirmDialog.action === 'clearPassword'
            ? '清空密码后，用户需使用新密码登录，且无法恢复！'
            : '注销用户后，该用户信息将被删除，且无法恢复！'"
          type="warning"
          :closable="false"
          show-icon
          style="margin-bottom: 15px;"
        ></el-alert>
        <p style="margin-bottom: 12px; color: #606266;">
          请两次输入目标用户工号 <strong style="color: #f56c6c;">{{ manageUserDialog.userCode }}</strong> 以确认操作：
        </p>
        <el-input
          v-model="confirmDialog.codeInput1"
          placeholder="请输入用户工号（第一次输入）"
          clearable
          size="medium"
          style="margin-bottom: 10px;"
        ></el-input>
        <el-input
          v-model="confirmDialog.codeInput2"
          placeholder="请再次输入用户工号（第二次输入）"
          clearable
          size="medium"
          @keyup.enter.native="executeManageAction"
        ></el-input>
      </div>
      <div slot="footer" class="dialog-footer">
        <el-button @click="confirmDialog.visible = false" size="small">取消</el-button>
        <el-button
          type="danger"
          size="small"
          :loading="confirmDialog.loading"
          @click="executeManageAction"
        >
          确认执行
        </el-button>
      </div>
    </el-dialog>
  </div>
</template>

<script>
// 导入用户信息工具类
import UserInfoUtils from '@/utils/userInfoUtils.js';
import UserInfoCard from '@/usersetting/UserInfoCard.vue';
import { setManagementList, getManagementList, clearUserPassword, cancelUserAccount,  updateUserPermission} from '@/api/mainapi.js';

export default {
  name: 'UserSettings',
  components: {
    UserInfoCard
  },
  computed: {
    /** ✅ 手机端要显示的列（prop 列表）
     *  card-table 的规则：手机模式下没传 mobileColumns 时，
     *  只渲染列定义里 showOnMobile === true 的列 → 不传的话列全没了（只剩空表）。
     *  这里把全部列都传进去，保证手机 / 电脑显示的列完全一致。 */
    managementMobileColumns() {
      return (this.managementListColumn || []).map(col => col.prop);
    },

    /** ✅ 勾选结果 → 传给后端的 urlPermission 字符串
     *  ⚠️ 按 permissionOptions 的 1~6 固定顺序拼，而不是勾选顺序：
     *     否则先勾「合同」再勾「项目」会拼出 '21'，全选也未必是 '123456'。
     *     一个都不勾 → ''（后端视为无任何模块权限） */
    permissionString() {
      return this.permissionOptions
        .filter(opt => this.permissionDialog.checked.indexOf(opt.value) !== -1)
        .map(opt => opt.value)
        .join('');
    }
  },
  data() {
    return {
      // 表格列配置
      managementListColumn: [
        {
          prop: 'userName',
          label: '用户名',
        },
        {
          prop: 'userCode',
          label: '工号'
        },
        {
          prop: 'department',
          label: '部门'
        },                
        {
          prop: 'role',
          label: '职位'
        },
        {
          prop: 'isAdmin',
          label: '管理员',
          custom: true,
          className: 'switch-cell-left',   // ✅ 手机端用来把开关改成靠左（见样式里的 .switch-cell-left）
          column: {
            type: 'switch',
            field: 'isAdmin',
          }
        },
        // {
        //   prop: 'resetPassword',
        //   label: '密码重置',
        //   custom: true,
        //   column: {
        //     type: 'resetPassword',
        //     field: 'resetPassword',
        //     width: '100',
        //     eventName: 'resetPasswordClick'
        //   }
        // }
      ],

      // // 重置密码对话框相关数据
      // resetPasswordDialog: {
      //   visible: false,
      //   userCode: '',
      //   username: '',
      //   newPassword: '',
      //   confirmPassword: '',
      //   index: -1
      // },
      // resetPasswordLoading: false,
      
      // 设为管理员相关数据
      setAdminUserCode: '',  // 要设置为管理员的工号
      setAdminLoading: false,  // 设置管理员按钮的加载状态

      // 管理用户信息弹窗相关数据
      manageUserDialog: {
        visible: false,
        userCode: ''  // 要操作的用户工号
      },
      // 操作确认弹窗（输入工号两次确认）
      confirmDialog: {
        visible: false,
        action: '',       // 操作类型：clearPassword 清空密码 / cancelAccount 注销用户
        codeInput1: '',   // 第一次输入的工号
        codeInput2: '',   // 第二次输入的工号
        loading: false    // 执行中的加载状态
      },

      // ✅ 修改用户权限弹窗（多选模块权限）
      permissionDialog: {
        visible: false,
        checked: []       // 选中的模块编码，如 ['1','2']（元素为字符串，便于和 value 直接比较）
      },
      // 模块选项：value 即后端 urlPermission 的编码（1项目 2合同 3生产 4产品 5部件 6交付）
      permissionOptions: [
        { value: '1', label: '项目' },
        { value: '2', label: '合同' },
        { value: '3', label: '生产' },
        { value: '4', label: '产品' },
        { value: '5', label: '部件' },
        { value: '6', label: '交付' }
      ],
      permissionLoading: false,
      
      // 表格数据和分页配置
      managementData: [],
      managementTotal: 0,
      managementPage: {
        currentPage: 1,
        pageSize: 50
      },
      
      // 当前登录用户信息（直接从内存获取）
      userInfo: {
        id: null,
        username: '',
        userCode: '',
        department: '',
        role: '',
        isAdmin: false
      },
      
      loading: false
    };
  },
  mounted() {
    // 验证用户登录状态
    if (!this.checkLoginStatus()) {
      return;
    }
    
    this.loadUserData();
  },
  methods: {
    /**
     * 检查登录状态
     */
    checkLoginStatus() {
      if (!UserInfoUtils.isLoggedIn()) {
        this.$message.warning('用户未登录，正在跳转到登录页...');
        setTimeout(() => {
          this.$router.push('/login');
        }, 1500);
        return false;
      }
      return true;
    },
    
    /**
     * 返回上一页
     * 无上一页历史时，返回可查看的最大模块主页（1项目最大→6交付最小，管理员→项目）
     */
    goBack() {
      if (this.$route.matched.length > 1) {
        this.$router.go(-1);
      } else {
        // 与驾驶舱/工艺流程/工位管理等页面 backHome 保持一致（见 UserInfoUtils.getDefaultHomeRoute）
        this.$router.push(UserInfoUtils.getDefaultHomeRoute());
      }
    },
    
    /**
     * 加载页面所有数据
     */
    async loadUserData() {
      // 直接从内存获取当前登录用户信息
      this.loadCurrentUserInfoFromMemory();
      
      // 加载管理员列表（表格数据）
      await this.loadManagementList();
    },
    
    /**
     * 从内存获取当前登录用户信息
     */
    loadCurrentUserInfoFromMemory() {
      try {
        // 使用 UserInfoUtils 直接从内存获取所有用户信息
        const userInfo = UserInfoUtils.getAllUserInfo();
        
        
        // 填充到组件数据
        this.userInfo = {
          id: null, // 本地存储中没有ID，如果API需要可以设为userCode
          username: userInfo.username,
          userCode: userInfo.userCode,
          department: userInfo.department,
          role: userInfo.position, // 假设职位对应role
          isAdmin: userInfo.isAdmin
        };
        
        
      } catch (error) {
        this.$message.error('获取用户信息失败: ' + error.message);
      }
    },
    
    /**
     * 加载管理员列表
     */
    async loadManagementList() {
      this.loading = true;
      // 检查当前用户是否有权限
      if (!UserInfoUtils.isAdmin()) {
        this.$message.warning('您没有权查看管理员列表');
        return;
      }
      try {
        const params = [];
        const response = await getManagementList(params);
        
        if (response.data.code === 200) {
          // 格式化数据
          this.managementData = this.formatManagementData(response.data.data['管理员名单：'] || []);
          this.managementTotal = this.managementData.length;

        } else {
          this.$message.error('获取管理员列表失败: ' + response.message);
        }
      } catch (error) {
        this.$message.error('获取管理员列表异常: ' + error.message);
      } finally {
        this.loading = false;
      }
    },
    
    /**
     * 格式化管理员数据
     */
    formatManagementData(managementList) {
      if (!managementList || !Array.isArray(managementList)) {
        return [];
      }
      
      // 获取当前用户的工号
      const currentUserCode = UserInfoUtils.getUserCode();
      
      return managementList.map((user, index) => {
        // 从 localStorage 获取当前登录用户信息来判断管理员状态
        const isCurrentUser = user.工号 === currentUserCode;
        
        return {
          id: index, 
          userName: user.姓名,
          userCode: user.工号,
          department: user.部门,
          role: user.职位,
          isAdmin: 1, // 当前用户使用本地存储的管理员状态
          isCurrentUser: isCurrentUser
        };
      });
    },
    
    /**
     * 处理设为管理员操作
     */
    async handleSetAdmin() {
      // 验证权限
      if (!UserInfoUtils.isAdmin()) {
        this.$message.warning('您没有权限设置管理员');
        return;
      }
      
      // 验证输入
      if (!this.setAdminUserCode || !this.setAdminUserCode.trim()) {
        this.$message.warning('请输入工号');
        return;
      }
      
      const userCode = this.setAdminUserCode.trim();
      
      // 验证是否是当前用户
      if (userCode === UserInfoUtils.getUserCode()) {
        this.$message.warning('不能设置自己为管理员（您已经是管理员）');
        this.setAdminUserCode = '';
        return;
      }
      
      this.setAdminLoading = true;
      
      try {
        // 调用 setManagementList 接口设置管理员权限
        const params = [{
          code: userCode,
          isManagement: "1",  // 设置为管理员
        }];
        
        const response = await setManagementList(params);
        
        if (response.data.code === 200) {
          this.$message.success(`工号 ${userCode} 已成功设置为管理员`);
          
          // 清空输入框
          this.setAdminUserCode = '';
          
          // 重新加载管理员列表
          await this.loadManagementList();
        } else {
          this.$message.error('设置管理员失败: ' + response.data.message);
        }
      } catch (error) {
        this.$message.error('设置管理员异常: ' + error.message);
      } finally {
        this.setAdminLoading = false;
      }
    },
    
    /**
     * 处理开关变化（设置管理员权限）
     */
    async handleSwitchChange(data) {
      // 检查当前用户是否有权限
      if (!UserInfoUtils.isAdmin()) {
        this.$message.warning('您没有权限修改管理员权限');
        await this.loadManagementList();
        return;
      }
      
      // 不能修改自己的管理员权限
      if (data.row.userCode === UserInfoUtils.getUserCode()) {
        this.$message.warning('不能修改自己的管理员权限');
        await this.loadManagementList();
        return;
      }
      
      try {
        const params = [{
          code: data.row.userCode,
          isManagement: data.value ? "1" : "0",  // 根据开关状态设置
        }];
        
        // 调用 setManagementList 接口更新管理员权限
        const response = await setManagementList(params);
        
        if (response.data.code === 200) {
          this.$message.success(`用户 "${data.row.userName}" 的管理员权限已${data.value ? '开启' : '关闭'}`);
          // 重新加载管理员列表
          await this.loadManagementList();
        } else {
          this.$message.error('权限变更失败: ' + response.message);
        }
      } catch (error) {
        this.$message.error('权限变更异常: ' + error.message);
      }
    },
    
    /**
     * 处理分页大小变化
     */
    handleManagementSizeChanges(pageSize) {
      this.managementPage.pageSize = pageSize;
      this.managementPage.currentPage = 1;
      this.loadManagementList();
    },
    
    /**
     * 处理当前页码变化
     */
    handleManagementCurrentChanges(currentPage) {
      this.managementPage.currentPage = currentPage;
      this.loadManagementList();
    },



    /**
     * 打开管理用户信息弹窗
     */
    openManageUserDialog() {
      // 检查当前用户是否有权限
      if (!UserInfoUtils.isAdmin()) {
        this.$message.warning('您没有权限管理用户信息');
        return;
      }
      this.manageUserDialog = {
        visible: true,
        userCode: ''
      };
    },

    /**
     * 打开操作确认弹窗
     * @param {string} action clearPassword 清空密码 / cancelAccount 注销用户
     */
    openConfirmDialog(action) {
      const userCode = this.manageUserDialog.userCode.trim();
      if (!userCode) {
        this.$message.warning('请输入要操作的用户工号');
        return;
      }
      // 不允许对自己执行操作
      if (userCode === UserInfoUtils.getUserCode()) {
        this.$message.warning('不能对当前登录用户执行此操作');
        return;
      }
      this.confirmDialog = {
        visible: true,
        action,
        codeInput1: '',
        codeInput2: '',
        loading: false
      };
    },

    /**
     * 管理用户信息弹窗关闭后重置
     */
    handleManageUserDialogClosed() {
      this.manageUserDialog.userCode = '';
    },

    /**
     * 操作确认弹窗关闭后重置
     */
    handleConfirmDialogClosed() {
      this.confirmDialog.action = '';
      this.confirmDialog.codeInput1 = '';
      this.confirmDialog.codeInput2 = '';
      this.confirmDialog.loading = false;
    },

    /**
     * 执行清空密码 / 注销用户操作（需输入工号两次确认）
     */
    async executeManageAction() {
      const targetCode = this.manageUserDialog.userCode.trim();
      const codeInput1 = this.confirmDialog.codeInput1.trim();
      const codeInput2 = this.confirmDialog.codeInput2.trim();

      // 校验两次输入
      if (!codeInput1 || !codeInput2) {
        this.$message.warning('请输入用户工号两次以确认操作');
        return;
      }
      if (codeInput1 !== codeInput2) {
        this.$message.warning('两次输入的工号不一致');
        return;
      }
      if (codeInput1 !== targetCode) {
        this.$message.warning('输入的工号与目标用户不匹配');
        return;
      }

      const isClearPassword = this.confirmDialog.action === 'clearPassword';
      this.confirmDialog.loading = true;

      try {
        // 调用对应接口：清空密码 / 注销用户
        const apiFn = isClearPassword ? clearUserPassword : cancelUserAccount;
        const response = await apiFn({ code: targetCode });

        if (response.data.code === 200) {
          this.$message.success(response.data.data || (isClearPassword ? '密码已清空' : '用户已注销'));

          // 关闭弹窗并重置
          this.confirmDialog.visible = false;
          this.manageUserDialog.visible = false;

          // 重新加载管理员列表
          await this.loadManagementList();
        } else {
          this.$message.error(response.data.message || '操作失败');
        }
      } catch (error) {
        this.$message.error(`${isClearPassword ? '清空密码' : '注销用户'}异常: ` + error.message);
      } finally {
        this.confirmDialog.loading = false;
      }
    },

    /**
     * 打开「修改用户权限」弹窗（沿用管理弹窗里已填的工号）
     */
    openPermissionDialog() {
      if (!this.manageUserDialog.userCode.trim()) {
        this.$message.warning('请先输入用户工号');
        return;
      }
      this.permissionDialog = {
        visible: true,
        checked: []      // 默认不勾选（改完即传空权限，避免误给权限）
      };
    },

    /**
     * 权限弹窗关闭后重置
     */
    handlePermissionDialogClosed() {
      this.permissionDialog.checked = [];
      this.permissionLoading = false;
    },

    /**
     * 提交用户权限
     * 入参：{ code: 用户工号, urlPermission: '123456' | '2' | '' }
     * 返回：code=200 成功；其余（如 500）按 message 提示失败
     */
    async submitUserPermission() {
      const targetCode = this.manageUserDialog.userCode.trim();
      if (!targetCode) {
        this.$message.warning('请先输入用户工号');
        return;
      }

      const urlPermission = this.permissionString;   // 全选 '123456' / 只选合同 '2' / 不选 ''
      this.permissionLoading = true;

      try {
        const response = await updateUserPermission({ code: targetCode, urlPermission });

        if (response.data.code === 200) {
          // 后端：{ code:200, message:'操作成功', data:'修改成功' }
          this.$message.success(response.data.data || response.data.message || '修改成功');
          this.permissionDialog.visible = false;
        } else {
          // 后端：{ code:500, message:'修改失败' }
          this.$message.error(response.data.message || '修改失败');
        }
      } catch (error) {
        this.$message.error('修改权限异常: ' + error.message);
      } finally {
        this.permissionLoading = false;
      }
    },
  }
};
</script>

<style scoped lang="scss">
.user-settings-container {
  padding: 20px;
  background: #f5f7fa;
  min-height: calc(100vh - 60px);
  display: flex;
  flex-direction: column;
  height: 100%;
  overflow: auto;
}

/* 头部容器样式 */
.settings-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  width: 100%;
  padding: 10px 0;
  margin-bottom: 20px;
  border-bottom: 1px solid #e6e6e6;
}

/* 标题样式 */
.settings-header .title {
  margin: 0;
  font-size: 24px;
  font-weight: bold;
  color: #333;
}

/* 返回按钮样式 */
.settings-header .back-button {
  margin-left: auto;
  font-size: 16px;
  color: #409eff;
  padding: 8px 12px;
}

/* 设为管理员样式 */
.set-admin-filter-content {
  display: flex;
  align-items: center;
  justify-content: space-between;
  width: 100%;
  padding: 8px 0;
}

/* 管理用户信息按钮 */
.manage-user-button {
  margin-left: auto;
  white-space: nowrap;
}

.set-admin-form {
  display: flex;
  align-items: center;
  gap: 8px;
}

.set-admin-label {
  font-size: 13px;
  color: #606266;
  white-space: nowrap;
}

/* 确保 user-info-card 样式 */
:deep(.user-info-card) {
  flex: 0.5;
  height: 200px;
  min-height: 200px;
}

/* 对话框按钮样式
   ⚠️ 管理用户信息弹窗的 footer 有 4 个按钮（取消 / 清空用户密码 / 注销用户信息 / 修改用户权限），
      按默认间距 + 默认内边距会超出 420px 弹窗宽度、把按钮挤到弹窗外。
      这里统一收紧：间距 10px→6px、按钮左右内边距 15px→9px，并允许换行兜底。 */
.dialog-footer {
  display: flex;
  justify-content: flex-end;
  gap: 6px;
  flex-wrap: wrap;      /* 兜底：空间不够时换行，绝不溢出弹窗 */
}

/* 收紧按钮内边距；
   同时清掉 Element 自带的「相邻按钮 margin-left:10px」——否则会和上面的 gap 叠加成 16px */
.dialog-footer ::v-deep .el-button {
  padding: 7px 9px;
  margin-left: 0;
}

/* ✅ 手机端（≤768px）优化 */
@media screen and (max-width: 768px) {
  /* ① 操作区改竖排：一行放不下「添加管理员 + 管理用户信息」，
       所以让「管理用户信息」整体换到下一行 */
  .set-admin-filter-content {
    display: flex !important;
    flex-direction: column !important;
    align-items: stretch !important;
    gap: 6px;
    padding: 6px 0 !important;
  }

  .set-admin-form {
    width: 100%;
    flex-wrap: nowrap;
    gap: 6px;

    /* 输入框是行内 style="width:150px"，必须用 !important 才改得动 */
    ::v-deep .el-input {
      width: auto !important;
      flex: 1 1 auto;
      min-width: 90px;
      margin-right: 6px !important;
    }
  }

  .set-admin-label {
    font-size: 12px;
    white-space: nowrap;
  }

  /* 「管理用户信息」按钮独占第二行 */
  .manage-user-button {
    margin-left: 0 !important;
    width: 100%;
  }

  /* ② 管理员列的开关靠左（列定义里加了 className: 'switch-cell-left'） */
  ::v-deep .switch-cell-left .cell {
    text-align: left !important;
  }

  /* ③ 高度分配：页面本身不滚动，卡片分高度，表格在自己内部滚动 */
  .user-settings-container {
    height: calc(100vh - 20px) !important;
    min-height: 0 !important;
    overflow: hidden !important;   /* 页面级滚动关掉，滚动条收敛到表格里 */
    padding: 10px !important;
    gap: 8px;
  }

  /* 标题栏：手机端字体变小、行高压矮 */
  .settings-header {
    flex: 0 0 auto;
    margin-bottom: 0 !important;
    padding: 4px 0 !important;
    line-height: 1.2;

    .title {
      font-size: 16px !important;   /* 原 24px */
      line-height: 22px !important;
    }

    .back-button {
      font-size: 13px !important;   /* 原 16px */
      padding: 2px 6px !important;  /* 原 8px 12px → 行高变矮 */
      line-height: 20px !important;
      height: 24px !important;
    }
  }

  /* 上方「个人信息」卡片：固定 180px */
  .user-info-card {
    flex: 0 0 150px !important;
    height: 150px !important;
    min-height: 150px !important;
    margin-bottom: 0 !important;
    overflow: hidden !important;
  }

  /* 下方「管理员信息」卡片：吃掉剩下的全部高度
     ⚠️ card-m 的 cardHeight 是行内 style，必须 !important 才能改 */
  .card-main {
    flex: 1 1 auto !important;
    height: auto !important;
    min-height: 0 !important;
    max-height: none !important;
  }

  /* 表格区域：不撑出卡片，内容多时由 el-table 内部出滚动条 */
  .card-main ::v-deep .card-box {
    flex: 1 1 auto;
    height: auto;
    min-height: 0;
    overflow: hidden;
  }
}
</style>